import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import LanguageToggle from './LanguageToggle';
import {
  House,
  Users,
  CalendarDots,
  Money,
  TShirt,
  Trophy,
  Volleyball,
  Cake,
  List,
  X,
  SignOut,
  User,
  Bell,
  CreditCard,
  Gear,
  Lightning,
  Wallet,
  ChartBar,
  CalendarCheck,
  Receipt,
  CalendarStar
} from '@phosphor-icons/react';

const LOGO_URL = "/logo-tk-dark.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: House },
  { path: '/students', label: 'Alunos', icon: Users },
  { path: '/birthdays', label: 'Aniversariantes', icon: Cake },
  { path: '/classes', label: 'Turmas Fixas', icon: CalendarDots },
  { path: '/turmas-horarios', label: 'Editar Turmas/Horários', icon: Gear },
  { path: '/courts', label: 'TK League', icon: Volleyball },
  { path: '/tk-league/torneio', label: 'Torneio TK League', icon: Trophy },
  { path: '/court-rental-admin', label: 'Aluguel Quadra', icon: Volleyball },
  { path: '/rankings', label: 'Rankings', icon: Trophy },
  { path: '/payments', label: 'Mensalidades', icon: Money },
  { path: '/all-payments', label: 'Todos Pagamentos', icon: Wallet },
  { path: '/relatorios', label: 'Relatórios', icon: ChartBar },
  { path: '/subscriptions', label: 'Assinaturas', icon: CreditCard },
  { path: '/subscriptions-billing', label: 'Cobrança (dia 30)', icon: CalendarCheck },
  { path: '/cobrancas', label: 'Cobrança Avulsa', icon: Receipt },
  { path: '/eventos', label: 'Eventos', icon: CalendarStar },
  { path: '/drop-in-admin', label: 'Aulas Avulsas', icon: Lightning },
  { path: '/open-play-admin', label: 'Open Play', icon: Volleyball },
  { path: '/notifications', label: 'Notificacoes', icon: Bell },
  { path: '/store', label: 'Uniformes', icon: TShirt },
  { path: '/settings', label: 'Configuracoes', icon: Gear },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/summary`, { withCredentials: true });
        setUnreadCount(data.unread);
      } catch {
        // ignore
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-[#E6E2D8] px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-[#F5F2EB] transition-colors"
            data-testid="mobile-menu-btn"
          >
            <List size={24} className="text-[#1A1A1A]" />
          </button>
          <h1 className="font-['Outfit'] font-semibold text-[#1A1A1A]">Tia Karol Sports</h1>
          <div className="w-10" />
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E6E2D8] z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#E6E2D8]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center p-1">
                  <img
                    src="/logo-tk-dark.png"
                    alt="Tia Karol Sports"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.parentElement.style.display = 'none';
                      e.target.parentElement.nextSibling.style.display = 'flex';
                    }}
                  />
                </div>
                <div className="w-10 h-10 bg-[#E85D04] rounded-lg items-center justify-center hidden">
                  <Volleyball size={24} className="text-white" weight="fill" />
                </div>
                <div className="flex flex-col">
                  <span className="font-['Outfit'] font-bold text-sm text-[#1A1A1A] leading-tight">
                    Tia Karol
                  </span>
                  <span className="font-['Outfit'] font-semibold text-xs text-[#1A1A1A] leading-tight">
                    Sports
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-lg hover:bg-[#F5F2EB]"
                data-testid="close-sidebar-btn"
              >
                <X size={20} className="text-[#73706A]" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const showBadge = item.path === '/notifications' && unreadCount > 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link ${isActive ? 'active' : ''} relative`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  <span className="font-medium">{item.label}</span>
                  {showBadge && (
                    <span className="ml-auto bg-[#9B2226] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-[#E6E2D8]">
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#F5F2EB]">
              <div className="w-8 h-8 bg-[#0077B6] rounded-full flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{user?.name}</p>
                <p className="text-xs text-[#73706A] truncate">{user?.email}</p>
              </div>
            </div>
            <div className="mt-3 flex justify-center">
              <LanguageToggle />
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-3 flex items-center gap-3 px-4 py-3 rounded-lg text-[#9B2226] hover:bg-red-50 transition-colors"
              data-testid="logout-btn"
            >
              <SignOut size={20} />
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
